import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { clientsData } from "@/mocks/home";

function getInitials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function getBgColor(name: string): string {
  const colors = [
    "bg-red-100 text-red-600",
    "bg-orange-100 text-orange-600",
    "bg-amber-100 text-amber-600",
    "bg-green-100 text-green-600",
    "bg-teal-100 text-teal-600",
    "bg-cyan-100 text-cyan-600",
    "bg-sky-100 text-sky-600",
    "bg-indigo-100 text-indigo-600",
    "bg-violet-100 text-violet-600",
    "bg-fuchsia-100 text-fuchsia-600",
    "bg-rose-100 text-rose-600",
    "bg-emerald-100 text-emerald-600",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export default function ClientsSection() {
  const { t } = useTranslation("home");
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const cardWidth = container.firstElementChild
      ? (container.firstElementChild as HTMLElement).offsetWidth + 16
      : 280;
    const scrollAmount = direction === "left" ? -cardWidth * 4 : cardWidth * 4;
    container.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  return (
    <section className="w-full bg-white py-12 md:py-20">
      <div className="w-full px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-magenta uppercase tracking-wide mb-3">
            {t("clients.title")}
          </h2>
          <p className="text-sm md:text-base text-text-muted">
            {t("clients.subtitle")}
          </p>
          <div className="w-16 h-1 bg-brandgreen mx-auto mt-4" />
        </div>

        <div className="relative">
          <button
            onClick={() => scroll("left")}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white border border-gray-200 rounded-full hover:border-brandgreen hover:text-brandgreen transition-colors cursor-pointer"
            aria-label="Anterior"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-lg" />
            </span>
          </button>

          <button
            onClick={() => scroll("right")}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white border border-gray-200 rounded-full hover:border-brandgreen hover:text-brandgreen transition-colors cursor-pointer"
            aria-label="Siguiente"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-lg" />
            </span>
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory px-12 md:px-14 pb-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {clientsData.map((client) => (
              <a
                key={client.name}
                href={client.website || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex-shrink-0 snap-start w-[calc(25%-12px)] min-w-[200px] md:min-w-0 flex flex-col items-center justify-center rounded-lg p-4 md:p-5 bg-gray-50 hover:bg-magenta/5 border border-transparent hover:border-magenta/20 transition-all duration-300 cursor-pointer"
              >
                <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center mb-3 overflow-hidden ${client.image ? "bg-white" : getBgColor(client.name)}`}>
                  {client.image ? (
                    <img
                      src={client.image}
                      alt={client.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xl md:text-2xl font-bold">
                      {getInitials(client.name)}
                    </span>
                  )}
                </div>
                <span className="text-sm font-semibold text-text text-center leading-tight">
                  {client.name}
                </span>
                {client.website && (
                  <span className="text-xs text-text-muted mt-1 group-hover:text-brandgreen transition-colors">
                    Visitar sitio <i className="ri-external-link-line ml-0.5" />
                  </span>
                )}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}